import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Dropdown,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, RefreshCw } from 'lucide-react';

import { ApiError } from '../api/client';
import { FlightDeckEvent, MigrationEventsResponse, getMigrationEvents } from '../api/migrations';
import RelativeTime from '../components/RelativeTime';
import { ACCENT, IDENTIFIER, NUMERIC, PLAIN_BUTTON, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-285 (FD-ADR-031) — the event log, replacing the filterless JSON-dump
// accordion.
//
// The event trail is the AUDIT RECORD for a device's migration. Two properties
// follow from that and shape everything here:
//
// 1. **Nothing is ever dropped.** The type filter's options are derived from the
//    events actually present, never from a hardcoded vocabulary — a fixed list
//    would silently omit any event type the console has not been taught, which
//    is precisely the event most worth seeing. An unrecognised status renders
//    verbatim on a neutral badge rather than being coerced to pass/fail.
//
// 2. **Field-level rendering degrades to raw, it does not replace it.** Known
//    envelope fields are stripped out and the REMAINING payload is rendered as
//    labelled rows — but every event keeps a raw-JSON disclosure, because an
//    event whose shape the UI cannot parse is the one an operator needs to read
//    byte for byte.
//
// Filters live in the URL (`?type=`, `?status=`), like every other filter in
// this console: shareable, reload-surviving, back/forward-aware, never
// localStorage (FD-ADR-031 §1, inherited from ADR-015).

/**
 * Fields the event ENVELOPE always carries. Everything else on an event is
 * payload the emitter stamped, and is what the field-level view renders.
 *
 * `partitionKey`/`etag`/`timestamp` are Table Storage artefacts that ride along
 * on some rows; they are envelope noise, not payload.
 */
const ENVELOPE_KEYS = new Set([
  'jobId',
  'rowKey',
  'partitionKey',
  'eventId',
  'eventSubType',
  'eventStatus',
  'serialNumber',
  'timeStamp',
  'processedAt',
  'etag',
  'timestamp',
]);

const ALL = '__all__';

export default function EventLogPage() {
  const { migrationId } = useParams<{ migrationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const jobId = searchParams.get('jobId')?.trim() || '';
  const typeFilter = searchParams.get('type') || ALL;
  const statusFilter = searchParams.get('status') || ALL;

  const detailPath = migrationId ? `/migrations/${encodeURIComponent(migrationId)}/overview` : '/migrations';

  const query = useQuery<MigrationEventsResponse>({
    queryKey: ['events', migrationId, jobId],
    queryFn: () => getMigrationEvents(migrationId!, jobId),
    enabled: Boolean(migrationId && jobId),
    retry: (failureCount, error) => {
      // 400 is the server's "missing jobId"; we already guard on the client.
      if (error instanceof ApiError && error.status === 400) return false;
      return failureCount < 1;
    },
  });

  // Newest first. ISO-8601 sorts correctly as a string, so no Date parsing and
  // no timezone drift.
  const events = useMemo<FlightDeckEvent[]>(
    () =>
      [...(query.data?.events ?? [])].sort((a, b) =>
        (b.timeStamp ?? '').localeCompare(a.timeStamp ?? ''),
      ),
    [query.data],
  );

  // Derived from the data, never hardcoded — see the header comment.
  const types = useMemo(
    () => [...new Set(events.map((e) => e.eventSubType).filter(Boolean))].sort(),
    [events],
  );
  const statuses = useMemo(
    () => [...new Set(events.map((e) => String(e.eventStatus)).filter(Boolean))].sort(),
    [events],
  );

  const filtered = useMemo(
    () =>
      events.filter(
        (e) =>
          (typeFilter === ALL || e.eventSubType === typeFilter) &&
          (statusFilter === ALL || String(e.eventStatus) === statusFilter),
      ),
    [events, typeFilter, statusFilter],
  );

  const setFilter = (key: 'type' | 'status', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === ALL) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const isFiltered = typeFilter !== ALL || statusFilter !== ALL;

  if (!jobId) {
    return (
      <div className="max-w-xl">
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>No device selected</MessageBarTitle>
            Pick a device from the campaign to see its event trail. The URL needs a{' '}
            <span className="font-mono text-xs">?jobId=…</span> query parameter.
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="subtle" size="small" onClick={() => navigate(detailPath)}>
              Back to campaign
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  if (query.isLoading) return <EventLogSkeleton detailPath={detailPath} />;

  if (query.isError) {
    return (
      <div className="flex flex-col gap-6">
        <BackLink to={detailPath} />
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not load events</MessageBarTitle>
            {errorMessage(query.error)}
          </MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="subtle"
              size="small"
              icon={<RefreshCw size={14} strokeWidth={1.5} />}
              onClick={() => query.refetch()}
            >
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  const serial = events[0]?.serialNumber;

  return (
    <div className="flex flex-col gap-6">
      <BackLink to={detailPath} />

      <header>
        <h1 className="text-[22px] font-semibold" style={{ color: TEXT.primary }}>
          Event log
        </h1>
        <div
          className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]"
          style={{ color: TEXT.secondary }}
        >
          {serial && (
            <span>
              Device <span className="text-xs" style={IDENTIFIER}>{serial}</span>
            </span>
          )}
          <span>
            {/* The count is stated as N of M whenever a filter is on, so a
                filtered view can never be mistaken for a short trail. */}
            <span style={NUMERIC}>{filtered.length}</span>
            {isFiltered && (
              <>
                {' of '}
                <span style={NUMERIC}>{events.length}</span>
              </>
            )}{' '}
            event{(isFiltered ? filtered.length : events.length) === 1 ? '' : 's'}
          </span>
          {/* jobId is a GUID capability — present because an operator
              correlating with server logs needs it, but last and quiet. */}
          <span style={{ color: TEXT.secondary }}>
            job <span className="font-mono text-[11px]">{jobId}</span>
          </span>
        </div>
      </header>

      {events.length > 0 && (
        <div className="flex flex-wrap items-end gap-3">
          <FilterDropdown
            id="event-type-filter"
            label="Event type"
            value={typeFilter}
            allLabel="All types"
            options={types}
            onChange={(v) => setFilter('type', v)}
          />
          <FilterDropdown
            id="event-status-filter"
            label="Status"
            value={statusFilter}
            allLabel="All statuses"
            options={statuses}
            onChange={(v) => setFilter('status', v)}
          />
          {isFiltered && (
            <Button size="small" appearance="subtle" onClick={() => setSearchParams(
              (() => {
                const next = new URLSearchParams(searchParams);
                next.delete('type');
                next.delete('status');
                return next;
              })(),
              { replace: true },
            )}>
              Clear filters
            </Button>
          )}
        </div>
      )}

      {events.length === 0 ? (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>No events yet</MessageBarTitle>
            This device has not emitted any events for this job. Events appear here as the
            device runtime progresses through its migration.
          </MessageBarBody>
        </MessageBar>
      ) : filtered.length === 0 ? (
        <MessageBar intent="info">
          <MessageBarBody>
            {/* Distinct from "no events yet": the trail is not empty, the
                filter is. Collapsing the two would read as a device that never
                reported. */}
            <MessageBarTitle>No events match these filters</MessageBarTitle>
            This device has <span className="font-mono tabular-nums">{events.length}</span>{' '}
            events in total.
          </MessageBarBody>
        </MessageBar>
      ) : (
        <div style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}>
          <Accordion collapsible multiple>
            {filtered.map((event) => (
              <AccordionItem
                key={event.rowKey}
                value={event.rowKey}
                style={{ borderTop: `1px solid ${STROKE.subtle}` }}
              >
                <AccordionHeader>
                  <div className="grid w-full grid-cols-[minmax(180px,240px)_minmax(140px,1fr)_auto] items-center gap-4 pr-2">
                    <div className="flex flex-col">
                      <span
                        className="font-mono text-[13px]"
                        style={{ color: TEXT.primary }}
                        title={event.timeStamp}
                      >
                        {event.timeStamp}
                      </span>
                      <span className="font-display text-[11px]" style={{ color: TEXT.secondary }}>
                        <RelativeTime iso={event.timeStamp} />
                      </span>
                    </div>
                    <span
                      className="font-display text-[13px] font-medium"
                      style={{ color: TEXT.primary }}
                    >
                      {event.eventSubType}
                    </span>
                    <div className="justify-self-end">
                      <StatusBadgeForEvent status={event.eventStatus} />
                    </div>
                  </div>
                </AccordionHeader>
                <AccordionPanel>
                  <EventPayload event={event} />
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
}

// ——— payload ————————————————————————————————————————————————————————————

function EventPayload({ event }: { event: FlightDeckEvent }) {
  const [showRaw, setShowRaw] = useState(false);

  const payload = useMemo(
    () => Object.entries(event).filter(([k]) => !ENVELOPE_KEYS.has(k)),
    [event],
  );

  return (
    <div className="flex flex-col gap-3 px-4 pb-4 pt-1">
      {payload.length === 0 ? (
        <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          {/* Said out loud. An empty panel would read as a rendering failure. */}
          This event carries no payload beyond its type, status and timestamp.
        </p>
      ) : (
        <dl className="grid grid-cols-[minmax(140px,220px)_1fr] gap-x-4 gap-y-1.5">
          {payload.map(([key, value]) => (
            <div key={key} className="contents">
              <dt
                className="font-display text-[11px] font-medium uppercase tracking-[0.06em]"
                style={{ color: TEXT.secondary }}
              >
                {key}
              </dt>
              <dd className="min-w-0 break-words font-mono text-xs" style={{ color: TEXT.primary }}>
                {renderValue(value)}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowRaw((v) => !v)}
          aria-expanded={showRaw}
          className={`${PLAIN_BUTTON} cursor-pointer font-display text-xs hover:underline`}
          style={{ color: ACCENT.hover }}
        >
          {showRaw ? 'Hide raw JSON' : 'Raw JSON'}
        </button>
        {showRaw && (
          <pre
            className="mt-2 overflow-x-auto p-3 font-mono text-xs leading-relaxed"
            style={{
              border: `1px solid ${STROKE.base}`,
              backgroundColor: SURFACE.rowFill,
              color: TEXT.primary,
            }}
          >
            {JSON.stringify(event, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

/**
 * A payload value as text.
 *
 * Objects and arrays are stringified rather than recursed: a nested renderer
 * would decide what to show, and this view's whole contract is that it shows
 * everything. The raw disclosure is one click away for anything dense.
 */
function renderValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// ——— chrome ——————————————————————————————————————————————————————————————

function BackLink({ to }: { to: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 self-start font-display text-sm no-underline hover:underline"
      style={{ color: ACCENT.hover }}
    >
      <ArrowLeft size={14} strokeWidth={1.5} />
      Back to campaign
    </Link>
  );
}

function FilterDropdown({
  id,
  label,
  value,
  allLabel,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  allLabel: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className="font-display text-[11px] font-medium uppercase tracking-[0.06em]"
        style={{ color: TEXT.secondary }}
      >
        {label}
      </label>
      <Dropdown
        id={id}
        aria-label={label}
        value={value === ALL ? allLabel : value}
        selectedOptions={[value]}
        onOptionSelect={(_, data) => onChange((data.optionValue as string) ?? ALL)}
        style={{ minWidth: '200px' }}
      >
        <Option value={ALL} text={allLabel}>
          {allLabel}
        </Option>
        {options.map((o) => (
          <Option key={o} value={o} text={o}>
            {o}
          </Option>
        ))}
      </Dropdown>
    </div>
  );
}

/**
 * EventHelper v1.2 emits a lowercase vocabulary (success, error, warning,
 * pending, pass, fail). An UNKNOWN value renders verbatim on a neutral badge —
 * never coerced to pass or fail, because guessing on an audit record is worse
 * than admitting the console does not know the word.
 */
function StatusBadgeForEvent({ status }: { status: FlightDeckEvent['eventStatus'] }) {
  const { color, label } = eventStatusBadge(status);
  return (
    <Badge appearance="tint" color={color}>
      {label}
    </Badge>
  );
}

export function eventStatusBadge(status: FlightDeckEvent['eventStatus']): {
  color: 'success' | 'danger' | 'warning' | 'informative';
  label: string;
} {
  switch (status) {
    case 'success':
    case 'pass':
      return { color: 'success', label: 'Success' };
    case 'error':
    case 'fail':
      return { color: 'danger', label: 'Failed' };
    case 'warning':
      return { color: 'warning', label: 'Warning' };
    case 'pending':
      return { color: 'informative', label: 'Pending' };
    default:
      return { color: 'informative', label: String(status) };
  }
}

function EventLogSkeleton({ detailPath }: { detailPath: string }) {
  return (
    <div className="flex flex-col gap-6">
      <BackLink to={detailPath} />
      <div className="flex flex-col gap-3">
        <Skeleton>
          <SkeletonItem size={32} style={{ width: '30%' }} />
        </Skeleton>
        <Skeleton>
          <SkeletonItem size={16} style={{ width: '60%' }} />
        </Skeleton>
      </div>
      <div style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="grid grid-cols-[minmax(180px,240px)_minmax(140px,1fr)_auto] items-center gap-4 px-4 py-3"
          >
            <Skeleton>
              <SkeletonItem size={16} />
            </Skeleton>
            <Skeleton>
              <SkeletonItem size={16} style={{ width: '70%' }} />
            </Skeleton>
            <Skeleton>
              <SkeletonItem size={16} style={{ width: '80px' }} />
            </Skeleton>
          </div>
        ))}
      </div>
    </div>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
